import React, { useCallback, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Shield, Loader2, AlertTriangle } from 'lucide-react';

/**
 * PrivacyModeOffDialog — password-gated dialog for turning Privacy Mode
 * OFF. A nosy person can't just tap the shield off; the member has to
 * prove they're still the member.
 *
 * Rendered by both the header shield and the Settings card so behaviour
 * is identical from either entry point.
 */
const PrivacyModeOffDialog = ({ open, onClose, onSubmit }) => {
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault?.();
    setSubmitting(true);
    setError('');
    const result = await onSubmit(password);
    setSubmitting(false);
    if (result.ok) {
      setPassword('');
      onClose();
    } else {
      setError(result.error || 'Incorrect password.');
    }
  }, [onSubmit, password, onClose]);

  if (!open) return null;
  return (
    <div
      data-testid="privacy-mode-off-dialog"
      className="fixed inset-0 z-[9998] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm mx-4 bg-white rounded-2xl shadow-2xl p-6 space-y-4"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
            <Shield className="w-5 h-5 text-emerald-700" aria-hidden="true" />
          </div>
          <div>
            <h2 className="text-lg font-serif font-bold text-slate-900">Turn Privacy Mode off</h2>
            <p className="text-sm text-slate-500">Enter your password to reveal your account.</p>
          </div>
        </div>
        <Input
          type="password"
          data-testid="privacy-mode-password-input"
          autoFocus
          value={password}
          onChange={(e) => { setPassword(e.target.value); setError(''); }}
          placeholder="Your password"
          disabled={submitting}
        />
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600" data-testid="privacy-mode-off-error">
            <AlertTriangle className="w-4 h-4" />
            <span>{error}</span>
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <Button
            type="button"
            variant="ghost"
            onClick={() => { setPassword(''); setError(''); onClose(); }}
            disabled={submitting}
            data-testid="privacy-mode-off-cancel"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            data-testid="privacy-mode-off-submit"
            disabled={submitting || !password}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying…</>) : 'Turn off'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PrivacyModeOffDialog;
